/**
 * Derives timeline grid metrics from zoom, weekend visibility, and header viewport width:
 * cell width (px/day), which calendar days to render, and total scrollable width.
 */
import {
  getShiftedDate,
  getWeekNumber,
  getWeekStart,
  toLocalDateKey,
} from "../../../../utils/datetime/core";
import {
  SCHEDULE_CELL_WIDTH_PX,
  SCHEDULE_MIN_VISIBLE_CELLS,
} from "../constants";

/**
 * Pixel width for one day column at the given zoom level.
 *
 * @param {number} zoomLevel Schedule zoom (1–3).
 * @returns {number}
 */
export function getTimelineCellWidth(zoomLevel) {
  return SCHEDULE_CELL_WIDTH_PX[zoomLevel] ?? SCHEDULE_CELL_WIDTH_PX[2];
}

/**
 * Derive cell width, visible days, grouping metadata, and total scroll width.
 *
 * @param {Object} params
 * @param {Date} params.startDate Left edge of the timeline strip.
 * @param {number} params.zoomLevel Schedule zoom (1–3).
 * @param {boolean} params.showWeekends When false, Sat/Sun are omitted from `visibleDays`.
 * @param {number} params.viewportWidth Header/grid viewport width in pixels.
 * @returns {{
 *   cellWidth: number,
 *   timelineWidth: number,
 *   visibleDays: Date[],
 *   monthGroups: Array<{ key: string, start: Date, days: number }>,
 *   weekGroups: Array<{ key: string, start: Date, weekNumber: number, days: number }>,
 * }}
 */
export function getTimelineLayout({
  startDate,
  zoomLevel,
  showWeekends,
  viewportWidth,
}) {
  const cellWidth = getTimelineCellWidth(zoomLevel);
  const minVisibleCells =
    SCHEDULE_MIN_VISIBLE_CELLS[zoomLevel] ?? SCHEDULE_MIN_VISIBLE_CELLS[2];

  const visibleCells = Math.max(
    minVisibleCells,
    Math.ceil((viewportWidth || 0) / cellWidth)
  );
  const horizonDays = showWeekends
    ? visibleCells
    : Math.ceil(visibleCells * (7 / 5));

  const allDays = [];
  for (let i = 0; i < horizonDays; i += 1) {
    allDays.push(getShiftedDate(startDate, i));
  }

  const visibleDays = showWeekends
    ? allDays
    : allDays.filter((day) => day.getDay() !== 0 && day.getDay() !== 6);

  const monthGroups = [];
  visibleDays.forEach((day) => {
    const key = `${day.getFullYear()}-${day.getMonth()}`;
    const last = monthGroups[monthGroups.length - 1];
    if (!last || last.key !== key) {
      monthGroups.push({ key, start: day, days: 1 });
    } else {
      last.days += 1;
    }
  });

  const weekGroups = [];
  visibleDays.forEach((day) => {
    const weekStart = getWeekStart(day);
    const key = toLocalDateKey(weekStart);
    const last = weekGroups[weekGroups.length - 1];
    if (!last || last.key !== key) {
      weekGroups.push({
        key,
        start: weekStart,
        weekNumber: getWeekNumber(weekStart),
        days: 1,
      });
    } else {
      last.days += 1;
    }
  });

  return {
    cellWidth,
    timelineWidth: visibleDays.length * cellWidth,
    visibleDays,
    monthGroups,
    weekGroups,
  };
}

